import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { createTourPackage } from '../redux/slices/packageSlice';
import AdminSidebar from '../Components/AdminSidebar';
import { v4 as uuidv4 } from 'uuid';

const AdminPackageUI = () => {
    const dispatch = useDispatch();
    const { loading, error } = useSelector((state) => state.package || {});
    const [activeSection, setActiveSection] = useState('createPackage');
    const [validationErrors, setValidationErrors] = useState([]);
    const [touched, setTouched] = useState({}); // Track touched fields
    const [formData, setFormData] = useState({
        title: '',
        tourType: '',
        originalPrice: '',
        discountedPrice: '',
        rating: '',
        reviewCount: '',
        duration: '',
        inclusions: [{ id: uuidv4(), label: '', icon: '' }],
        images: [{ file: null, alt: 'alt' }],
        durationOptions: [
            {
                days: '',
                originalPrice: '',
                discountedPrice: '',
                image: null,
                destinations: [''],
                highlights: [''],
                route: '',
                inclusionExclusions: [{ label: '', category: 'inclusion', icon: [] }],
                knowBeforeYouGo: [''],
                itineraries: [
                    {
                        day: 'Day 1',
                        title: '',
                        description: '',
                        sharedTransfer: { type: '', from: '', stop: 'check', to: '' },
                        checkInTime: '',
                        checkOutTime: '',
                        hotels: [{ name: '', rating: '', image: null }],
                        inclusions: { breakfast: 'Included', lunch: 'Not Included', dinner: 'Not Included' },
                        activity: { name: '', image: null },
                    },
                ],
            },
        ],
    });

    const validateForm = () => {
        const errors = [];

        try {
            // Package-level required fields
            if (!formData?.title?.trim()) errors.push('Package title is required.');
            if (!formData?.tourType?.trim()) errors.push('Package tour type is required.');
            if (!formData?.originalPrice || isNaN(formData.originalPrice) || formData.originalPrice <= 0)
                errors.push('Valid original price is required.');
            if (!formData?.discountedPrice || isNaN(formData.discountedPrice) || formData.discountedPrice <= 0)
                errors.push('Valid discounted price is required.');
            if (formData?.rating === '' || formData.rating < 0 || formData.rating > 5)
                errors.push('Rating must be between 0 and 5.');
            if (formData?.reviewCount === '' || formData.reviewCount < 0)
                errors.push('Review count must be a non-negative number.');
            if (!formData?.duration?.trim()) errors.push('Duration is required.');

            // Inclusions validation
            if (!Array.isArray(formData?.inclusions) || formData.inclusions.length === 0) {
                errors.push('At least one inclusion is required.');
            } else if (formData.inclusions.some((inc) => !inc?.label?.trim() || !inc?.icon?.trim())) {
                errors.push('All inclusions must have a valid label and icon.');
            }

            // Images validation
            if (!Array.isArray(formData?.images) || formData.images.length === 0) {
                errors.push('At least one package image is required.');
            } else if (formData.images.some((img) => !img?.file)) {
                errors.push('All package images must have a file.');
            }

            // Duration options validation
            if (!Array.isArray(formData?.durationOptions) || formData.durationOptions.length === 0) {
                errors.push('At least one duration option is required.');
            } else {
                formData.durationOptions.forEach((opt, idx) => {
                    if (!opt?.days || opt.days <= 0)
                        errors.push(`Days for duration option ${idx + 1} must be a positive number.`);
                    if (!opt?.originalPrice || isNaN(opt.originalPrice) || opt.originalPrice <= 0)
                        errors.push(`Original price for duration option ${idx + 1} must be a positive number.`);
                    if (!opt?.discountedPrice || isNaN(opt.discountedPrice) || opt.discountedPrice <= 0)
                        errors.push(`Discounted price for duration option ${idx + 1} must be a positive number.`);
                    if (!opt?.image)
                        errors.push(`Image for duration option ${idx + 1} is required.`);

                    // Duration-specific required fields
                    if (!Array.isArray(opt?.destinations) || opt.destinations.length === 0) {
                        errors.push(`At least one destination is required for duration option ${idx + 1}.`);
                    } else if (opt.destinations.some((d) => !d.trim())) {
                        errors.push(`All destinations for duration option ${idx + 1} must be filled.`);
                    }

                    if (!Array.isArray(opt?.highlights) || opt.highlights.length === 0) {
                        errors.push(`At least one highlight is required for duration option ${idx + 1}.`);
                    } else if (opt.highlights.some((h) => !h.trim())) {
                        errors.push(`All highlights for duration option ${idx + 1} must be filled.`);
                    }

                    if (!opt?.route?.trim()) {
                        errors.push(`Route for duration option ${idx + 1} is required.`);
                    }

                    if (!Array.isArray(opt?.inclusionExclusions) || opt.inclusionExclusions.length === 0) {
                        errors.push(`At least one inclusion/exclusion is required for duration option ${idx + 1}.`);
                    } else if (opt.inclusionExclusions.some((item) => !item?.label?.trim() || !item?.category?.trim())) {
                        errors.push(`All inclusion/exclusion labels and categories for duration option ${idx + 1} must be filled.`);
                    }

                    if (!Array.isArray(opt?.knowBeforeYouGo) || opt.knowBeforeYouGo.length === 0) {
                        errors.push(`At least one knowBeforeYouGo tip is required for duration option ${idx + 1}.`);
                    } else if (opt.knowBeforeYouGo.some((tip) => !tip.trim())) {
                        errors.push(`All knowBeforeYouGo tips for duration option ${idx + 1} must be filled.`);
                    }

                    if (!Array.isArray(opt?.itineraries) || opt.itineraries.length === 0) {
                        errors.push(`At least one itinerary is required for duration option ${idx + 1}.`);
                    } else {
                        opt.itineraries.forEach((it, itIdx) => {
                            if (!it?.day?.trim()) errors.push(`Day for itinerary ${itIdx + 1} (duration option ${idx + 1}) is required.`);
                            if (!it?.title?.trim()) errors.push(`Title for itinerary ${itIdx + 1} (duration option ${idx + 1}) is required.`);
                            if (!it?.description?.trim()) errors.push(`Description for itinerary ${itIdx + 1} (duration option ${idx + 1}) is required.`);
                            if (!it?.sharedTransfer?.type?.trim()) errors.push(`Shared transfer type for itinerary ${itIdx + 1} (duration option ${idx + 1}) is required.`);
                            if (!it?.sharedTransfer?.from?.trim()) errors.push(`Transfer from for itinerary ${itIdx + 1} (duration option ${idx + 1}) is required.`);
                            if (!it?.sharedTransfer?.stop?.trim()) errors.push(`Transfer stop for itinerary ${itIdx + 1} (duration option ${idx + 1}) is required.`);
                            if (!it?.sharedTransfer?.to?.trim()) errors.push(`Transfer to for itinerary ${itIdx + 1} (duration option ${idx + 1}) is required.`);
                        });
                    }
                });
            }

            setValidationErrors(errors);
            return errors.length === 0;
        } catch (err) {
            console.error('Validation Error:', err);
            setValidationErrors(['An error occurred during validation. Please check the form data.']);
            return false;
        }
    };

    const markTouched = (fieldPath) => {
        setTouched((prev) => ({ ...prev, [fieldPath]: true }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name.includes('Price') ? parseFloat(value) || '' : value,
        }));
        markTouched(name);
        setValidationErrors([]);
    };

    const handleArrayChange = (durationIndex, field, index, value) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? { ...opt, [field]: opt[field].map((item, j) => (j === index ? value : item)) }
                    : opt
            ),
        }));
        markTouched(`durationOptions[${durationIndex}].${field}[${index}]`);
        setValidationErrors([]);
    };

    const handleInclusionChange = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            inclusions: prev.inclusions.map((inc, i) =>
                i === index ? { ...inc, [field]: value } : inc
            ),
        }));
        markTouched(`inclusions[${index}].${field}`);
        setValidationErrors([]);
    };

    const handleImageChange = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            images: prev.images.map((img, i) =>
                i === index ? { ...img, [field]: value } : img
            ),
        }));
        markTouched(`images[${index}].${field}`);
        setValidationErrors([]);
    };

    const handleDurationOptionChange = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === index
                    ? { ...opt, [field]: field === 'days' || field === 'originalPrice' || field === 'discountedPrice' ? parseFloat(value) || '' : value }
                    : opt
            ),
        }));
        markTouched(`durationOptions[${index}].${field}`);
        setValidationErrors([]);
    };

    const handleItineraryChange = (durationIndex, itineraryIndex, field, value) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? {
                        ...opt,
                        itineraries: opt.itineraries.map((it, j) =>
                            j === itineraryIndex ? { ...it, [field]: value } : it
                        ),
                    }
                    : opt
            ),
        }));
        markTouched(`durationOptions[${durationIndex}].itineraries[${itineraryIndex}].${field}`);
        setValidationErrors([]);
    };

    const handleSharedTransferChange = (durationIndex, itineraryIndex, field, value) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? {
                        ...opt,
                        itineraries: opt.itineraries.map((it, j) =>
                            j === itineraryIndex
                                ? { ...it, sharedTransfer: { ...it.sharedTransfer, [field]: value } }
                                : it
                        ),
                    }
                    : opt
            ),
        }));
        markTouched(`durationOptions[${durationIndex}].itineraries[${itineraryIndex}].sharedTransfer.${field}`);
        setValidationErrors([]);
    };

    const handleHotelChange = (durationIndex, itineraryIndex, hotelIndex, field, value) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? {
                        ...opt,
                        itineraries: opt.itineraries.map((it, j) =>
                            j === itineraryIndex
                                ? {
                                    ...it,
                                    hotels: it.hotels.map((h, hIdx) =>
                                        hIdx === hotelIndex
                                            ? { ...h, [field]: field === 'rating' ? parseFloat(value) || '' : value }
                                            : h
                                    ),
                                }
                                : it
                        ),
                    }
                    : opt
            ),
        }));
        markTouched(`durationOptions[${durationIndex}].itineraries[${itineraryIndex}].hotels[${hotelIndex}].${field}`);
        setValidationErrors([]);
    };

    const handleItineraryInclusionChange = (durationIndex, itineraryIndex, meal, value) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? {
                        ...opt,
                        itineraries: opt.itineraries.map((it, j) =>
                            j === itineraryIndex
                                ? { ...it, inclusions: { ...it.inclusions, [meal]: value } }
                                : it
                        ),
                    }
                    : opt
            ),
        }));
        markTouched(`durationOptions[${durationIndex}].itineraries[${itineraryIndex}].inclusions.${meal}`);
        setValidationErrors([]);
    };

    const handleActivityChange = (durationIndex, itineraryIndex, field, value) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? {
                        ...opt,
                        itineraries: opt.itineraries.map((it, j) =>
                            j === itineraryIndex
                                ? { ...it, activity: { ...it.activity, [field]: value } }
                                : it
                        ),
                    }
                    : opt
            ),
        }));
        markTouched(`durationOptions[${durationIndex}].itineraries[${itineraryIndex}].activity.${field}`);
        setValidationErrors([]);
    };

    const handleInclusionExclusionChange = (durationIndex, index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? {
                        ...opt,
                        inclusionExclusions: opt.inclusionExclusions.map((item, j) =>
                            j === index ? { ...item, [field]: value } : item
                        ),
                    }
                    : opt
            ),
        }));
        markTouched(`durationOptions[${durationIndex}].inclusionExclusions[${index}].${field}`);
        setValidationErrors([]);
    };

    const handleKnowBeforeYouGoChange = (durationIndex, index, value) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? {
                        ...opt,
                        knowBeforeYouGo: opt.knowBeforeYouGo.map((tip, j) => (j === index ? value : tip)),
                    }
                    : opt
            ),
        }));
        markTouched(`durationOptions[${durationIndex}].knowBeforeYouGo[${index}]`);
        setValidationErrors([]);
    };

    const addArrayItem = (durationIndex, field) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? {
                        ...opt,
                        [field]:
                            field === 'destinations' || field === 'highlights' || field === 'knowBeforeYouGo'
                                ? [...opt[field], '']
                                : field === 'inclusionExclusions'
                                    ? [...opt[field], { label: '', category: 'inclusion', icon: [] }]
                                    : [
                                        ...opt[field],
                                        {
                                            day: `Day ${opt.itineraries.length + 1}`,
                                            title: '',
                                            description: '',
                                            sharedTransfer: { type: '', from: '', stop: 'check', to: '' },
                                            checkInTime: '',
                                            checkOutTime: '',
                                            hotels: [{ name: '', rating: '', image: null }],
                                            inclusions: { breakfast: 'Included', lunch: 'Not Included', dinner: 'Not Included' },
                                            activity: { name: '', image: null },
                                        },
                                    ],
                    }
                    : opt
            ),
        }));
        setValidationErrors([]);
    };

    const addDurationOption = () => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: [
                ...prev.durationOptions,
                {
                    days: '',
                    originalPrice: '',
                    discountedPrice: '',
                    image: null,
                    destinations: [''],
                    highlights: [''],
                    route: '',
                    inclusionExclusions: [{ label: '', category: 'inclusion', icon: [] }],
                    knowBeforeYouGo: [''],
                    itineraries: [
                        {
                            day: 'Day 1',
                            title: '',
                            description: '',
                            sharedTransfer: { type: '', from: '', stop: '', to: '' },
                            checkInTime: '',
                            checkOutTime: '',
                            hotels: [{ name: '', rating: '', image: null }],
                            inclusions: { breakfast: 'Included', lunch: 'Not Included', dinner: 'Not Included' },
                            activity: { name: '', image: null },
                        },
                    ],
                },
            ],
        }));
        setValidationErrors([]);
    };

    const addHotel = (durationIndex, itineraryIndex) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? {
                        ...opt,
                        itineraries: opt.itineraries.map((it, j) =>
                            j === itineraryIndex
                                ? { ...it, hotels: [...it.hotels, { name: '', rating: '', image: null }] }
                                : it
                        ),
                    }
                    : opt
            ),
        }));
        setValidationErrors([]);
    };

    const addTopLevelArrayItem = (field) => {
        setFormData((prev) => ({
            ...prev,
            [field]:
                field === 'inclusions'
                    ? [...prev[field], { id: uuidv4(), label: '', icon: 'M8 16l4-4m0 0l4 4m-4-4V4' }]
                    : [...prev[field], { file: null, alt: '', label: '' }],
        }));
        setValidationErrors([]);
    };

    const removeArrayItem = (durationIndex, field, index) => {
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? { ...opt, [field]: opt[field].filter((_, j) => j !== index) }
                    : opt
            ),
        }));
        setValidationErrors([]);
    };

    const removeTopLevelArrayItem = (field, index) => {
        if (field === 'inclusions' && formData.inclusions.length <= 1) {
            toast.error('At least one inclusion is required.', { position: 'top-right', autoClose: 5000 });
            return;
        }
        if (field === 'images' && formData.images.length <= 1) {
            toast.error('At least one image is required.', { position: 'top-right', autoClose: 5000 });
            return;
        }
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index),
        }));
        setValidationErrors([]);
    };

    const removeDurationOption = (index) => {
        if (formData.durationOptions.length <= 1) {
            toast.error('At least one duration option is required.', { position: 'top-right', autoClose: 5000 });
            return;
        }
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.filter((_, i) => i !== index),
        }));
        setValidationErrors([]);
    };

    const removeItinerary = (durationIndex, itineraryIndex) => {
        if (formData.durationOptions[durationIndex].itineraries.length <= 1) {
            toast.error('At least one itinerary is required per duration option.', { position: 'top-right', autoClose: 5000 });
            return;
        }
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? {
                        ...opt,
                        itineraries: opt.itineraries
                            .filter((_, j) => j !== itineraryIndex)
                            .map((it, j) => ({ ...it, day: `Day ${j + 1}` })),
                    }
                    : opt
            ),
        }));
        setValidationErrors([]);
    };

    const removeHotel = (durationIndex, itineraryIndex, hotelIndex) => {
        if (formData.durationOptions[durationIndex].itineraries[itineraryIndex].hotels.length <= 1) {
            toast.error('At least one hotel is required per itinerary.', { position: 'top-right', autoClose: 5000 });
            return;
        }
        setFormData((prev) => ({
            ...prev,
            durationOptions: prev.durationOptions.map((opt, i) =>
                i === durationIndex
                    ? {
                        ...opt,
                        itineraries: opt.itineraries.map((it, j) =>
                            j === itineraryIndex
                                ? { ...it, hotels: it.hotels.filter((_, hIdx) => hIdx !== hotelIndex) }
                                : it
                        ),
                    }
                    : opt
            ),
        }));
        setValidationErrors([]);
    };

    const handleSubmit = async () => {
        toast.dismiss();
        // Mark all fields as touched on submit
        const newTouched = {};
        Object.keys(formData).forEach((key) => {
            if (typeof formData[key] === 'string') newTouched[key] = true;
        });
        formData.inclusions.forEach((_, i) => {
            newTouched[`inclusions[${i}].label`] = true;
            newTouched[`inclusions[${i}].icon`] = true;
        });
        formData.images.forEach((_, i) => {
            newTouched[`images[${i}].file`] = true;
            // newTouched[`images[${i}].alt`] = true;
        });
        formData.durationOptions.forEach((opt, i) => {
            newTouched[`durationOptions[${i}].days`] = true;
            newTouched[`durationOptions[${i}].originalPrice`] = true;
            newTouched[`durationOptions[${i}].discountedPrice`] = true;
            newTouched[`durationOptions[${i}].image`] = true;
            newTouched[`durationOptions[${i}].route`] = true;
            opt.destinations.forEach((_, j) => {
                newTouched[`durationOptions[${i}].destinations[${j}]`] = true;
            });
            opt.highlights.forEach((_, j) => {
                newTouched[`durationOptions[${i}].highlights[${j}]`] = true;
            });
            opt.inclusionExclusions.forEach((_, j) => {
                newTouched[`durationOptions[${i}].inclusionExclusions[${j}].label`] = true;
                newTouched[`durationOptions[${i}].inclusionExclusions[${j}].category`] = true;
            });
            opt.knowBeforeYouGo.forEach((_, j) => {
                newTouched[`durationOptions[${i}].knowBeforeYouGo[${j}]`] = true;
            });
            opt.itineraries.forEach((it, j) => {
                newTouched[`durationOptions[${i}].itineraries[${j}].day`] = true;
                newTouched[`durationOptions[${i}].itineraries[${j}].title`] = true;
                newTouched[`durationOptions[${i}].itineraries[${j}].description`] = true;
                newTouched[`durationOptions[${i}].itineraries[${j}].sharedTransfer.type`] = true;
                newTouched[`durationOptions[${i}].itineraries[${j}].sharedTransfer.from`] = true;
                // newTouched[`durationOptions[${i}].itineraries[${j}].sharedTransfer.stop`] = true;
                newTouched[`durationOptions[${i}].itineraries[${j}].sharedTransfer.to`] = true;
            });
        });
        setTouched(newTouched);

        if (!validateForm()) {
            validationErrors.forEach((err) =>
                toast.error(err, {
                    position: 'top-right',
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                })
            );
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('tourType', formData.tourType);
        formDataToSend.append('originalPrice', formData.originalPrice);
        formDataToSend.append('discountedPrice', formData.discountedPrice);
        formDataToSend.append('rating', formData.rating);
        formDataToSend.append('reviewCount', formData.reviewCount);
        formDataToSend.append('duration', formData.duration);
        formDataToSend.append('inclusions', JSON.stringify(formData.inclusions));
        formDataToSend.append(
            'images',
            JSON.stringify(
                formData.images.map((img) => ({
                    url: '',
                    alt: img.alt || 'Tour image',
                    label: img.label || '',
                }))
            )
        );
        formData.images.forEach((img, index) => {
            if (img.file) formDataToSend.append(`images`, img.file);
        });

        const durationOptionsToSend = formData.durationOptions.map((opt) => ({
            days: parseInt(opt.days),
            originalPrice: parseFloat(opt.originalPrice),
            discountedPrice: parseFloat(opt.discountedPrice),
            image: '',
            destinations: opt.destinations.filter((d) => d.trim()),
            highlights: opt.highlights.filter((h) => h.trim()),
            route: opt.route,
            inclusionExclusions: opt.inclusionExclusions,
            knowBeforeYouGo: opt.knowBeforeYouGo.filter((tip) => tip.trim()).map((tip) => ({ tip })),
            itineraries: opt.itineraries.map((it) => ({
                ...it,
                sharedTransfer: {
                    type: it.sharedTransfer.type || '',
                    from: it.sharedTransfer.from || '',
                    stop: it.sharedTransfer.stop || 'check',
                    to: it.sharedTransfer.to || '',
                },
                hotels: it.hotels.map((h) => ({ ...h, image: '' })),
                activity: { ...it.activity, image: '' },
            })),
        }));

        formDataToSend.append('durationOptions', JSON.stringify(durationOptionsToSend));

        formData.durationOptions.forEach((opt, optIndex) => {
            if (opt.image) formDataToSend.append(`durationOptionsImages`, opt.image);
            opt.itineraries.forEach((it, itIndex) => {
                it.hotels.forEach((hotel, hIndex) => {
                    if (hotel.image) formDataToSend.append(`itineraryHotelImages`, hotel.image);
                });
                if (it.activity.image) formDataToSend.append(`itineraryActivityImages`, it.activity.image);
            });
        });

        try {
            const result = await dispatch(createTourPackage(formDataToSend)).unwrap();
            toast.success('Tour package created successfully!', {
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            setFormData({
                title: '',
                tourType: '',
                originalPrice: '',
                discountedPrice: '',
                rating: '',
                reviewCount: '',
                duration: '',
                inclusions: [{ id: uuidv4(), label: '', icon: 'M8 16l4-4m0 0l4 4m-4-4V4' }],
                images: [{ file: null, alt: '', label: '' }],
                durationOptions: [{
                    days: '',
                    price: '',
                    image: null,
                    destinations: [''],
                    highlights: [''],
                    route: '',
                    inclusionExclusions: [{ label: '', category: 'inclusion', icon: [] }],
                    knowBeforeYouGo: [''],
                    itineraries: [{
                        day: 'Day 1',
                        title: '',
                        description: '',
                        sharedTransfer: { type: '', from: '', stop: '', to: '' },
                        checkInTime: '',
                        checkOutTime: '',
                        hotels: [{ name: '', rating: '', image: null }],
                        inclusions: { breakfast: 'Included', lunch: 'Not Included', dinner: 'Not Included' },
                        activity: { name: '', image: null },
                    }],
                }],
            });
            setTouched({});
        } catch (err) {
            const errorMessage = err.message || err || 'Failed to create package.';
            toast.error(errorMessage, {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    const getInputClass = (fieldPath, value, isRequired = true) => {
        if (!touched[fieldPath]) return 'border-gray-300';
        if (isRequired && (!value || (typeof value === 'string' && !value.trim()))) return 'border-red-500';
        return 'border-gray-300';
    };

    const getFileInputClass = (fieldPath, file, isRequired = true) => {
        if (!touched[fieldPath]) return 'border-gray-300';
        if (isRequired && !file) return 'border-red-500';
        return 'border-gray-300';
    };

    return (
        <div className="flex min-h-screen bg-gray-100">
            <AdminSidebar setActiveSection={setActiveSection} activeSection={activeSection} />
            <div className="ml-72 flex-1 p-6 sm:p-8 lg:p-10 max-w-5xl mx-auto">
                <h1 className="text-3xl font-bold text-blue-800 mb-8">Create New Package</h1>
                {activeSection === 'createPackage' && (
                    <div className="space-y-6 bg-white p-6 rounded-lg shadow-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-blue-800">Package Title</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                        'title',
                                        formData.title
                                    )}`}
                                    placeholder="e.g., Hello Tour | FREE Excursion"
                                    required
                                    aria-required="true"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-blue-800">Package Type</label>
                                <select
                                    name="tourType"
                                    value={formData.tourType}
                                    onChange={handleInputChange}
                                    className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                        'tourType',
                                        formData.tourType
                                    )}`}
                                    required
                                    aria-required="true"
                                >
                                    <option value="" disabled>
                                        Select package type
                                    </option>
                                    <option value="Domestic">Domestic</option>
                                    <option value="International">International</option>
                                    <option value="Short">Short</option>
                                    <option value="Customised">Customised</option>
                                    <option value="Activities">Activities</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-blue-800">Original Price (INR)</label>
                                <input
                                    type="number"
                                    name="originalPrice"
                                    value={formData.originalPrice}
                                    onChange={handleInputChange}
                                    className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                        'originalPrice',
                                        formData.originalPrice
                                    )}`}
                                    placeholder="e.g., 226100"
                                    min="0"
                                    step="0.01"
                                    required
                                    aria-required="true"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-blue-800">Discounted Price (INR)</label>
                                <input
                                    type="number"
                                    name="discountedPrice"
                                    value={formData.discountedPrice}
                                    onChange={handleInputChange}
                                    className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                        'discountedPrice',
                                        formData.discountedPrice
                                    )}`}
                                    placeholder="e.g., 170000"
                                    min="0"
                                    step="0.01"
                                    required
                                    aria-required="true"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-blue-800">Rating (0-5)</label>
                                <input
                                    type="number"
                                    name="rating"
                                    value={formData.rating}
                                    onChange={handleInputChange}
                                    className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                        'rating',
                                        formData.rating
                                    )}`}
                                    step="0.1"
                                    min="0"
                                    max="5"
                                    required
                                    aria-required="true"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-blue-800">Review Count</label>
                                <input
                                    type="number"
                                    name="reviewCount"
                                    value={formData.reviewCount}
                                    onChange={handleInputChange}
                                    className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                        'reviewCount',
                                        formData.reviewCount
                                    )}`}
                                    min="0"
                                    required
                                    aria-required="true"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-blue-800">Duration</label>
                                <input
                                    type="text"
                                    name="duration"
                                    value={formData.duration}
                                    onChange={handleInputChange}
                                    className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                        'duration',
                                        formData.duration
                                    )}`}
                                    placeholder="e.g., 6D/5N"
                                    required
                                    aria-required="true"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-blue-800">Inclusions</label>
                            {formData.inclusions.map((inclusion, index) => (
                                <div
                                    key={inclusion.id}
                                    className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 mb-4 bg-gray-50 p-4 rounded-md"
                                >
                                    <select
                                        value={inclusion.label}
                                        onChange={(e) => {
                                            const selectedLabel = e.target.value;
                                            const iconMap = {
                                                Flight: 'M12 2l10 10-5 3-3 5-10-10z',
                                                Transfer: 'M8 16l4-4m0 0l4 4m-4-4V4',
                                                Food: 'M12 2a10 10 0 0110 10H2a10 10 0 0110-10z',
                                                Tickets: 'M4 6h16M4 12h16m-7 6h7',
                                                Hotels: 'M3 7l9-4 9 4v11a2 2 0 01-2 2H5a2 2 0 01-2-2V7z',
                                                'Sight Seeing': 'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
                                                'Tour manager': 'M16 7a4 4 0 11-8 0 4 4 0 018 0z',
                                            };
                                            handleInclusionChange(index, 'label', selectedLabel);
                                            handleInclusionChange(index, 'icon', iconMap[selectedLabel] || '');
                                        }}
                                        className={`w-full sm:w-2/3 p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                            `inclusions[${index}].label`,
                                            inclusion.label
                                        )}`}
                                        required
                                        aria-required="true"
                                    >
                                        <option value="" disabled>
                                            Select Inclusion
                                        </option>
                                        {['Flight', 'Transfer', 'Food', 'Tickets', 'Hotels', 'Sight Seeing', 'Tour manager'].map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                    {formData.inclusions.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeTopLevelArrayItem('inclusions', index)}
                                            className="mt-2 sm:mt-0 text-red-600 hover:text-red-800 transition-colors"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => addTopLevelArrayItem('inclusions')}
                                className="text-blue-600 hover:text-yellow-500 transition-colors"
                            >
                                + Add Inclusion
                            </button>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-blue-800">Package Images</label>
                            {formData.images.map((img, index) => (
                                <div key={index} className="mb-4 p-4 bg-gray-50 rounded-md">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleImageChange(index, 'file', e.target.files[0])}
                                        className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getFileInputClass(
                                            `images[${index}].file`,
                                            img.file
                                        )}`}
                                        required
                                        aria-required="true"
                                    />
                                    {/* <input
                                        type="text"
                                        value={img.label}
                                        onChange={(e) => handleImageChange(index, 'label', e.target.value)}
                                        className="mt-2 w-full p-3 border border-gray-300 rounded-md focus:ring-yellow-400 focus:border-yellow-400"
                                        placeholder="Image label"
                                    /> */}
                                    {img.file && (
                                        <p className="text-sm text-gray-600 mt-2">{img.file.name}</p>
                                    )}
                                    {formData.images.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeTopLevelArrayItem('images', index)}
                                            className="mt-2 text-red-600 hover:text-red-800 transition-colors"
                                        >
                                            Remove Image
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => addTopLevelArrayItem('images')}
                                className="text-blue-600 hover:text-yellow-500 transition-colors"
                            >
                                + Add Image
                            </button>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-blue-800">Duration Options</label>
                            {formData.durationOptions.map((option, durationIndex) => (
                                <div key={durationIndex} className="border border-gray-200 p-6 rounded-lg mb-6 bg-gray-50 shadow-md">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-semibold text-blue-800">Duration Option {durationIndex + 1}</h3>
                                        {formData.durationOptions.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeDurationOption(durationIndex)}
                                                className="text-red-600 hover:text-red-800 transition-colors"
                                            >
                                                Remove Duration Option
                                            </button>
                                        )}
                                    </div>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-blue-800">Days</label>
                                                <input
                                                    type="number"
                                                    value={option.days}
                                                    onChange={(e) => handleDurationOptionChange(durationIndex, 'days', e.target.value)}
                                                    className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                        `durationOptions[${durationIndex}].days`,
                                                        option.days
                                                    )}`}
                                                    placeholder="Days"
                                                    min="1"
                                                    required
                                                    aria-required="true"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-blue-800">Original Price (INR)</label>
                                                <input
                                                    type="number"
                                                    value={option.originalPrice}
                                                    onChange={(e) => handleDurationOptionChange(durationIndex, 'originalPrice', e.target.value)}
                                                    className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                        `durationOptions[${durationIndex}].originalPrice`,
                                                        option.originalPrice
                                                    )}`}
                                                    placeholder="e.g., 20000"
                                                    min="0"
                                                    step="0.01"
                                                    required
                                                    aria-required="true"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-blue-800">Discounted Price (INR)</label>
                                                <input
                                                    type="number"
                                                    value={option.discountedPrice}
                                                    onChange={(e) => handleDurationOptionChange(durationIndex, 'discountedPrice', e.target.value)}
                                                    className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                        `durationOptions[${durationIndex}].discountedPrice`,
                                                        option.discountedPrice
                                                    )}`}
                                                    placeholder="e.g., 17000"
                                                    min="0"
                                                    step="0.01"
                                                    required
                                                    aria-required="true"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-blue-800">Image</label>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleDurationOptionChange(durationIndex, 'image', e.target.files[0])}
                                                    className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getFileInputClass(
                                                        `durationOptions[${durationIndex}].image`,
                                                        option.image
                                                    )}`}
                                                    required
                                                    aria-required="true"
                                                />
                                                {option.image && <p className="text-sm text-gray-600 mt-2">{option.image.name}</p>}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-blue-800">Destinations</label>
                                            {option.destinations.map((dest, index) => (
                                                <div key={index} className="flex items-center mb-3">
                                                    <input
                                                        type="text"
                                                        value={dest}
                                                        onChange={(e) => handleArrayChange(durationIndex, 'destinations', index, e.target.value)}
                                                        className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                            `durationOptions[${durationIndex}].destinations[${index}]`,
                                                            dest
                                                        )}`}
                                                        placeholder="e.g., 1 Day in Oslo"
                                                        required
                                                        aria-required="true"
                                                    />
                                                    {option.destinations.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeArrayItem(durationIndex, 'destinations', index)}
                                                            className="ml-3 text-red-600 hover:text-red-800 transition-colors"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => addArrayItem(durationIndex, 'destinations')}
                                                className="text-blue-600 hover:text-yellow-500 transition-colors"
                                            >
                                                + Add Destination
                                            </button>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-blue-800">Route</label>
                                            <input
                                                type="text"
                                                value={option.route}
                                                onChange={(e) => handleDurationOptionChange(durationIndex, 'route', e.target.value)}
                                                className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                    `durationOptions[${durationIndex}].route`,
                                                    option.route
                                                )}`}
                                                placeholder="e.g., Oslo → Stockholm → Helsinki"
                                                required
                                                aria-required="true"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-blue-800">Highlights</label>
                                            {option.highlights.map((highlight, index) => (
                                                <div key={index} className="flex items-center mb-3">
                                                    <input
                                                        type="text"
                                                        value={highlight}
                                                        onChange={(e) => handleArrayChange(durationIndex, 'highlights', index, e.target.value)}
                                                        className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                            `durationOptions[${durationIndex}].highlights[${index}]`,
                                                            highlight
                                                        )}`}
                                                        placeholder={`Highlight ${index + 1}`}
                                                        required
                                                        aria-required="true"
                                                    />
                                                    {option.highlights.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeArrayItem(durationIndex, 'highlights', index)}
                                                            className="ml-3 text-red-600 hover:text-red-800 transition-colors"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => addArrayItem(durationIndex, 'highlights')}
                                                className="text-blue-600 hover:text-yellow-500 transition-colors"
                                            >
                                                + Add Highlight
                                            </button>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-blue-800">Inclusions and Exclusions</label>
                                            {option.inclusionExclusions.map((item, index) => (
                                                <div
                                                    key={index}
                                                    className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 mb-4 bg-gray-50 p-4 rounded-md"
                                                >
                                                    <input
                                                        type="text"
                                                        value={item.label}
                                                        onChange={(e) => handleInclusionExclusionChange(durationIndex, index, 'label', e.target.value)}
                                                        className={`w-full sm:w-2/3 p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                            `durationOptions[${durationIndex}].inclusionExclusions[${index}].label`,
                                                            item.label
                                                        )}`}
                                                        placeholder="e.g., Visa assistance"
                                                        required
                                                        aria-required="true"
                                                    />
                                                    <select
                                                        value={item.category}
                                                        onChange={(e) => handleInclusionExclusionChange(durationIndex, index, 'category', e.target.value)}
                                                        className={`w-full sm:w-1/3 p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                            `durationOptions[${durationIndex}].inclusionExclusions[${index}].category`,
                                                            item.category
                                                        )}`}
                                                        required
                                                        aria-required="true"
                                                    >
                                                        <option value="inclusion">Inclusion</option>
                                                        <option value="exclusion">Exclusion</option>
                                                    </select>
                                                    {option.inclusionExclusions.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeArrayItem(durationIndex, 'inclusionExclusions', index)}
                                                            className="mt-2 sm:mt-0 text-red-600 hover:text-red-800 transition-colors"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => addArrayItem(durationIndex, 'inclusionExclusions')}
                                                className="text-blue-600 hover:text-yellow-500 transition-colors"
                                            >
                                                + Add Inclusion/Exclusion
                                            </button>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-blue-800">Know Before You Go</label>
                                            {option.knowBeforeYouGo.map((tip, index) => (
                                                <div key={index} className="flex items-center mb-3">
                                                    <input
                                                        type="text"
                                                        value={tip}
                                                        onChange={(e) => handleKnowBeforeYouGoChange(durationIndex, index, e.target.value)}
                                                        className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                            `durationOptions[${durationIndex}].knowBeforeYouGo[${index}]`,
                                                            tip
                                                        )}`}
                                                        placeholder="e.g., Carry your passport"
                                                        required
                                                        aria-required="true"
                                                    />
                                                    {option.knowBeforeYouGo.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeArrayItem(durationIndex, 'knowBeforeYouGo', index)}
                                                            className="ml-3 text-red-600 hover:text-red-800 transition-colors"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => addArrayItem(durationIndex, 'knowBeforeYouGo')}
                                                className="text-blue-600 hover:text-yellow-500 transition-colors"
                                            >
                                                + Add Tip
                                            </button>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-blue-800">Itineraries</label>
                                            {option.itineraries.map((itinerary, itineraryIndex) => (
                                                <div
                                                    key={itineraryIndex}
                                                    className="border border-gray-200 p-6 rounded-lg mb-6 bg-gray-50 shadow-md"
                                                >
                                                    <div className="flex justify-between items-center mb-4">
                                                        <h3 className="text-lg font-semibold text-blue-800">Itineraries by Day {itineraryIndex + 1}</h3>
                                                        {option.itineraries.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeItinerary(durationIndex, itineraryIndex)}
                                                                className="text-red-600 hover:text-red-800 transition-colors"
                                                            >
                                                                Remove Day
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="space-y-4">
                                                        <div>
                                                            <p className="mt-1 w-full p-3 bg-gray-100 rounded-md text-blue-800">{itinerary.day}</p>
                                                        </div>
                                                        <input
                                                            type="text"
                                                            value={itinerary.title}
                                                            onChange={(e) => handleItineraryChange(durationIndex, itineraryIndex, 'title', e.target.value)}
                                                            className={`w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                                `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].title`,
                                                                itinerary.title
                                                            )}`}
                                                            placeholder="e.g., Arrival in Kerala"
                                                            required
                                                            aria-required="true"
                                                        />
                                                        <textarea
                                                            value={itinerary.description}
                                                            onChange={(e) => handleItineraryChange(durationIndex, itineraryIndex, 'description', e.target.value)}
                                                            className={`w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                                `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].description`,
                                                                itinerary.description
                                                            )}`}
                                                            placeholder="Description"
                                                            rows="4"
                                                            required
                                                            aria-required="true"
                                                        />
                                                        <div>
                                                            <label className="block text-sm font-medium text-blue-800">Shared Transfer</label>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                                                                <input
                                                                    type="text"
                                                                    value={itinerary.sharedTransfer.type}
                                                                    onChange={(e) =>
                                                                        handleSharedTransferChange(durationIndex, itineraryIndex, 'type', e.target.value)
                                                                    }
                                                                    className={`w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                                        `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].sharedTransfer.type`,
                                                                        itinerary.sharedTransfer.type
                                                                    )}`}
                                                                    placeholder="Transfer Type (e.g., Coach)"
                                                                    required
                                                                    aria-required="true"
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={itinerary.sharedTransfer.from}
                                                                    onChange={(e) =>
                                                                        handleSharedTransferChange(durationIndex, itineraryIndex, 'from', e.target.value)
                                                                    }
                                                                    className={`w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                                        `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].sharedTransfer.from`,
                                                                        itinerary.sharedTransfer.from
                                                                    )}`}
                                                                    placeholder="Transfer From"
                                                                    required
                                                                    aria-required="true"
                                                                />
                                                                {/* <input
                                                                    type="text"
                                                                    value={itinerary.sharedTransfer.stop}
                                                                    onChange={(e) =>
                                                                        handleSharedTransferChange(durationIndex, itineraryIndex, 'stop', e.target.value)
                                                                    }
                                                                    className={`w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                                        `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].sharedTransfer.stop`,
                                                                        itinerary.sharedTransfer.stop
                                                                    )}`}
                                                                    placeholder="Transfer Stop"
                                                                    required
                                                                    aria-required="true"
                                                                /> */}
                                                                <input
                                                                    type="text"
                                                                    value={itinerary.sharedTransfer.to}
                                                                    onChange={(e) =>
                                                                        handleSharedTransferChange(durationIndex, itineraryIndex, 'to', e.target.value)
                                                                    }
                                                                    className={`w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                                        `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].sharedTransfer.to`,
                                                                        itinerary.sharedTransfer.to
                                                                    )}`}
                                                                    placeholder="Transfer To"
                                                                    required
                                                                    aria-required="true"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                            <input
                                                                type="text"
                                                                value={itinerary.checkInTime}
                                                                onChange={(e) =>
                                                                    handleItineraryChange(durationIndex, itineraryIndex, 'checkInTime', e.target.value)
                                                                }
                                                                className={`w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                                    `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].checkInTime`,
                                                                    itinerary.checkInTime,
                                                                    false
                                                                )}`}
                                                                placeholder="Check-In Time (e.g., 2:00 PM)"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={itinerary.checkOutTime}
                                                                onChange={(e) =>
                                                                    handleItineraryChange(durationIndex, itineraryIndex, 'checkOutTime', e.target.value)
                                                                }
                                                                className={`w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                                    `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].checkOutTime`,
                                                                    itinerary.checkOutTime,
                                                                    false
                                                                )}`}
                                                                placeholder="Check-Out Time (e.g., 11:00 AM)"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-blue-800">Hotels</label>
                                                            {itinerary.hotels.map((hotel, hIndex) => (
                                                                <div
                                                                    key={hIndex}
                                                                    className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 mb-3 bg-white p-4 rounded-md shadow-sm"
                                                                >
                                                                    <input
                                                                        type="text"
                                                                        value={hotel.name}
                                                                        onChange={(e) =>
                                                                            handleHotelChange(durationIndex, itineraryIndex, hIndex, 'name', e.target.value)
                                                                        }
                                                                        className={`w-full sm:w-1/3 p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                                            `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].hotels[${hIndex}].name`,
                                                                            hotel.name,
                                                                            false
                                                                        )}`}
                                                                        placeholder="Hotel Name"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        value={hotel.rating}
                                                                        onChange={(e) =>
                                                                            handleHotelChange(durationIndex, itineraryIndex, hIndex, 'rating', e.target.value)
                                                                        }
                                                                        className={`w-full sm:w-1/4 p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                                            `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].hotels[${hIndex}].rating`,
                                                                            hotel.rating,
                                                                            false
                                                                        )}`}
                                                                        placeholder="Rating (e.g., 4.5)"
                                                                        min="0"
                                                                        step="0.1"
                                                                    />
                                                                    <div className="w-full sm:w-1/2">
                                                                        <input
                                                                            type="file"
                                                                            accept="image/*"
                                                                            onChange={(e) =>
                                                                                handleHotelChange(durationIndex, itineraryIndex, hIndex, 'image', e.target.files[0])
                                                                            }
                                                                            className={`w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getFileInputClass(
                                                                                `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].hotels[${hIndex}].image`,
                                                                                hotel.image,
                                                                                false
                                                                            )}`}
                                                                        />
                                                                        {hotel.image && <p className="text-sm text-gray-600 mt-2">{hotel.image.name}</p>}
                                                                    </div>
                                                                    {itinerary.hotels.length > 1 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => removeHotel(durationIndex, itineraryIndex, hIndex)}
                                                                            className="mt-2 sm:mt-0 text-red-600 hover:text-red-800 transition-colors"
                                                                        >
                                                                            Remove
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            <button
                                                                type="button"
                                                                onClick={() => addHotel(durationIndex, itineraryIndex)}
                                                                className="text-blue-600 hover:text-yellow-500 transition-colors"
                                                            >
                                                                + Add Hotel
                                                            </button>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-blue-800">Inclusions</label>
                                                            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mt-2">
                                                                {['breakfast', 'lunch', 'dinner'].map((meal) => (
                                                                    <div key={meal} className="flex items-center">
                                                                        <label className="mr-2 capitalize text-blue-800">{meal}</label>
                                                                        <select
                                                                            value={itinerary.inclusions[meal]}
                                                                            onChange={(e) =>
                                                                                handleItineraryInclusionChange(durationIndex, itineraryIndex, meal, e.target.value)
                                                                            }
                                                                            className="p-3 border border-gray-300 rounded-md focus:ring-yellow-400 focus:border-yellow-400"
                                                                        >
                                                                            <option>Included</option>
                                                                            <option>Not Included</option>
                                                                        </select>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-blue-800">Activity</label>
                                                            <input
                                                                type="text"
                                                                value={itinerary.activity.name}
                                                                onChange={(e) =>
                                                                    handleActivityChange(durationIndex, itineraryIndex, 'name', e.target.value)
                                                                }
                                                                className={`mt-1 w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getInputClass(
                                                                    `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].activity.name`,
                                                                    itinerary.activity.name,
                                                                    false
                                                                )}`}
                                                                placeholder="Activity Name"
                                                            />
                                                            <div className="mt-2">
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    onChange={(e) =>
                                                                        handleActivityChange(durationIndex, itineraryIndex, 'image', e.target.files[0])
                                                                    }
                                                                    className={`w-full p-3 border rounded-md focus:ring-yellow-400 focus:border-yellow-400 ${getFileInputClass(
                                                                        `durationOptions[${durationIndex}].itineraries[${itineraryIndex}].activity.image`,
                                                                        itinerary.activity.image,
                                                                        false
                                                                    )}`}
                                                                />
                                                                {itinerary.activity.image && (
                                                                    <p className="text-sm text-gray-600 mt-2">{itinerary.activity.image.name}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => addArrayItem(durationIndex, 'itineraries')}
                                                className="bg-blue-600 text-white p-3 rounded-md hover:bg-blue-700 transition-colors w-full"
                                            >
                                                + Add Another Day
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addDurationOption}
                                className="bg-blue-600 text-white p-3 rounded-md hover:bg-blue-700 transition-colors w-full"
                            >
                                + Add Duration Option
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading}
                            className={`w-full p-3 rounded-md font-medium transition-colors ${loading ? 'bg-gray-400 text-gray-700 cursor-not-allowed' : 'bg-yellow-400 text-blue-800 hover:bg-yellow-500'
                                }`}
                            aria-disabled={loading}
                        >
                            {loading ? (
                                <span className="flex items-center justify-center">
                                    <svg
                                        className="animate-spin h-5 w-5 mr-2 text-blue-800"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                                    </svg>
                                    Creating...
                                </span>
                            ) : (
                                'Create Package'
                            )}
                        </button>
                    </div>
                )}
                {activeSection === 'allPackages' && (
                    <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-lg">
                        <h2 className="text-2xl font-bold text-blue-800 mb-6">All Packages</h2>
                        <p className="text-gray-600">List of all packages will be displayed here.</p>
                    </div>
                )}
                {activeSection === 'allBanners' && (
                    <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-lg">
                        <h2 className="text-2xl font-bold text-blue-800 mb-6">All Banners</h2>
                        <p className="text-gray-600">List of all banners will be displayed here.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPackageUI;